import { useState, useEffect, useCallback } from 'react';

export interface TaskAssignmentForemanAreaRow {
  foremanArea: {
    id: number;
    name: string;
    serviceArea: { id: number; name: string; code: string | null };
  };
  plannedCount: number;
  activeCount: number;
  activeMarkedCompleteCount: number;
  internalReviewCount: number;
  drillTargets: {
    planned: TaskAssignmentDrillTarget;
    active: TaskAssignmentDrillTarget;
    activeMarkedAsComplete: TaskAssignmentDrillTarget;
    internalReview: TaskAssignmentDrillTarget;
  };
}

interface TaskAssignmentDrillTarget {
  routineCount: number;
  quantifiedCount: number;
  singleTaskId: number | null;
  singleTaskType: 'Routine' | 'Quantified' | null;
}

export interface TaskAssignmentSummaryData {
  summary: {
    tasksWithWorkIdCount: number;
    distinctWorkIdsCount: number;
    tasksWithAnyAssigneeCount: number;
  };
  byTaskStatus: Array<{ status: string; count: number }>;
  byWorkIdStatus: Array<{ status: string; count: number }>;
  byForemanAreaQuantified: TaskAssignmentForemanAreaRow[];
  byForemanAreaRoutine: TaskAssignmentForemanAreaRow[];
}

interface TaskAssignmentSummaryFilter {
  foremanAreaId?: number;
  serviceAreaId?: number;
}

interface UseWorkIdTaskAssignmentSummaryReturn {
  data: TaskAssignmentSummaryData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  lastUpdated: Date | null;
}

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Fetches Work ID ↔ task assignment aggregates for the home dashboard.
 * Server includes only ASSIGNED Work IDs and tasks in PLANNED / ACTIVE / ACTIVE_MARKED_AS_COMPLETE / INTERNAL_REVIEW.
 * Scoped by foreman or service area like other Work ID widgets.
 */
export function useWorkIdTaskAssignmentSummary(
  filter?: TaskAssignmentSummaryFilter | null
): UseWorkIdTaskAssignmentSummaryReturn {
  const [data, setData] = useState<TaskAssignmentSummaryData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filter?.foremanAreaId != null) {
        params.append('foremanAreaId', String(filter.foremanAreaId));
      }
      if (filter?.serviceAreaId != null) {
        params.append('serviceAreaId', String(filter.serviceAreaId));
      }
      const queryString = params.toString();
      const url = `${BASE_URL}/work-ids/task-assignment-summary${queryString ? `?${queryString}` : ''}`;
      const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch task assignment summary: ${response.statusText}`);
      }
      const json = await response.json();
      setData(json.data ?? null);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch task assignment summary'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filter?.foremanAreaId, filter?.serviceAreaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    lastUpdated,
  };
}
